import { useCopyToClipboard } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { INCENTIVE_SALES_STAGE_MAP } from 'shared'
import { toast } from 'sonner'
import type { OpportunityIncentive } from '@/lib'
import { convertPeriodOnToFiscalPeriod, convertPeriodToPaymentMonth } from '@/lib/period-utils'
import { useAcceptIncentive } from './useAcceptIncentive'
import { useOpportunityIncentiveAccepts, useOpportunityIncentives } from './useOpportunityIncentiveQueries'
import { usePeriodSelection } from './usePeriodSelection'

/**
 * インセンティブ確認ページ全体のロジックを統合したカスタムhook \
 * データ取得、フィルタリング、確認処理、クリップボードコピー機能を提供
 */
export function useIncentiveConfirmPage() {
  const [dialogOpen, setDialogOpen] = useState(false)

  // データフェッチング
  const { data: incentivesData, isLoading: isLoadingIncentives, error: incentivesError } = useOpportunityIncentives()
  const { data: acceptsData, isLoading: isLoadingAccepts } = useOpportunityIncentiveAccepts()
  const acceptIncentiveMutation = useAcceptIncentive()

  const isLoading = isLoadingIncentives || isLoadingAccepts

  // 期間選択ロジック
  const { selectedPeriod, setSelectedPeriod, availablePeriods, filteredIncentives, isIncentiveAccepted } =
    usePeriodSelection({
      incentives: incentivesData?.incentives,
      accepts: acceptsData?.accepts,
    })

  // インセンティブ金額の合計
  const totalIncentiveAmount = useMemo(() => {
    return filteredIncentives.reduce((sum, inc) => sum + (inc.incentive_amount || 0), 0)
  }, [filteredIncentives])

  const incentiveHeaders = [
    '管理ID',
    'SF案件名',
    '売上情報ID',
    '売上月',
    'オポ金額',
    'Sales Stage',
    '失注前Sales Stage',
    '係数',
    '報酬金額',
    '支払対象期間',
    '備考',
    '更新日時',
  ]

  const extractIncentiveValues = useCallback(
    (item: OpportunityIncentive) => [
      item.opportunity_id,
      item.salesforce_project_name || '',
      item.sales_id || '',
      item.sales_on,
      item.sales_amount,
      item.sales_stage_id ? (INCENTIVE_SALES_STAGE_MAP[item.sales_stage_id]?.text ?? 'ー') : 'ー',
      item.previously_closed_sales_stage_id
        ? (INCENTIVE_SALES_STAGE_MAP[item.previously_closed_sales_stage_id]?.text ?? 'ー')
        : 'ー',
      ((item.incentive_coefficient ?? 0) * 100).toFixed(1),
      item.incentive_amount,
      convertPeriodOnToFiscalPeriod(item.period),
      item.notes || '',
      DateTime.fromSeconds(Number(item.updated_at)).toFormat('yyyy/MM/dd HH:mm:ss'),
    ],
    [],
  )

  const copyToClipboard = useCopyToClipboard(incentiveHeaders, extractIncentiveValues)

  // インセンティブ確認ダイアログを開く
  const handleOpenConfirmDialog = useCallback(() => {
    // 既に確認済みの場合は何もしない
    if (isIncentiveAccepted) return
    setDialogOpen(true)
  }, [isIncentiveAccepted])

  // インセンティブ確認を実行
  const handleConfirmIncentive = useCallback(async () => {
    // "FY25上期" → "YYYY-MM" 形式に変換してAPIに送信
    const periodMonth = convertPeriodToPaymentMonth(selectedPeriod)
    if (!periodMonth) {
      toast.error('期間の変換に失敗しました')
      return
    }

    await acceptIncentiveMutation.mutateAsync({ period: periodMonth })
    setDialogOpen(false)
  }, [selectedPeriod, acceptIncentiveMutation])

  return {
    // 状態
    selectedPeriod,
    setSelectedPeriod,
    dialogOpen,
    setDialogOpen,
    // データ
    filteredIncentives,
    availablePeriods,
    isLoading,
    error: incentivesError,
    isIncentiveAccepted,
    totalIncentiveAmount,
    isProcessing: acceptIncentiveMutation.isPending,
    // 関数
    copyToClipboard,
    handleOpenConfirmDialog,
    handleConfirmIncentive,
  }
}
